import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { File, IMQCarrierFiles } from 'types/policyService';
import { getPolicyByGroupId, getPreSignUrl } from 'api/operations/policyService';
import { retryRequestFor } from 'global/utils/retryRequestFor';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { downloadFileByLink } from 'global/utils/downloadFileByLink';
import { buildPdfLink } from 'global/services-helpers/pdfService';
import ModalDelegate from 'store/general/modalDelegate';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';

export default class DownloadIMQsPageStore {
  private readonly _domainParams: QuoteFlowDomainParams;
  public readonly flowDelegate: QuoteFlowDelegate;
  private _imqFiles: Record<string, IMQCarrierFiles> = {};
  private _selectedCarrier = '';

  public warningModalDelegate = new ModalDelegate();

  public get carrierOptions(): OptionType[] {
    return Object.keys(this._imqFiles).map((carrier) => ({ label: carrier, value: carrier }));
  }

  public get selectedCarrier(): string {
    return this._selectedCarrier;
  }

  private get _selectedCarrierFiles(): IMQCarrierFiles | undefined {
    return this._imqFiles[this._selectedCarrier];
  }

  public get policyPdfs(): File[] {
    return this._selectedCarrierFiles?.pdfs ?? [];
  }

  public get finalZip(): File | undefined {
    return this._selectedCarrierFiles?.finalZip;
  }

  public get creationDate(): string {
    return this.policyPdfs[0]?.creationDate || '';
  }

  public setSelectedCarrier(carrier: string): void {
    this._selectedCarrier = carrier;
  }

  private async _loadPolicyFiles(): Promise<void> {
    try {
      const policy = await retryRequestFor(() => getPolicyByGroupId(this._domainParams.groupId), 10000, 2000);
      runInAction(() => {
        this._imqFiles = policy.imqFiles ?? {};
        const carriers = Object.keys(this._imqFiles);
        this._selectedCarrier = carriers[0] ?? '';
      });
    } catch {
      runInAction(() => {
        this.warningModalDelegate.open();
      });
    }
  }

  public async getInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await this.flowDelegate.getFlowInitialData();
    await this._loadPolicyFiles();
    this.flowDelegate.setIsInitialDataLoading(false);
  }

  // eslint-disable-next-line class-methods-use-this
  public downloadPdfFile(id: string, name: string): void {
    const downloadLink = buildPdfLink(id);
    downloadFileByLink(name, downloadLink);
  }

  public async downloadFinalZip(): Promise<void> {
    if (this.finalZip?.id) {
      const zipLink = await getPreSignUrl(this.finalZip.id);
      downloadByUrl(zipLink);
    }
  }

  constructor(params: QuoteFlowDomainParams) {
    this._domainParams = params;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: params,
      nextPageId: QuoteRouteV2Id.QUOTE_CHECKLIST,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const DownloadIMQsPageStoreContext = createContext(new DownloadIMQsPageStore({} as QuoteFlowDomainParams));

export const useDownloadIMQsPageStore = (): DownloadIMQsPageStore => {
  return useContext(DownloadIMQsPageStoreContext);
};

export { DownloadIMQsPageStoreContext };
